import { FC, useMemo, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { TextInput, Button, Spinner } from '../../';
import { HiOutlineCheck } from 'react-icons/hi';
import { StewardReportDetail } from '../../../services/dtos';

interface WeeklyEntryGridProps {
  allDetails: StewardReportDetail[];
  weekDates: Date[];
  isReadOnly: boolean;
  onDaySave: (date: Date, uniqueMemberId: string, dayData: DayData) => Promise<void>;
  uniqueMemberId?: string;
  stewardReportId?: number;
  currentRates?: {
    regTimeRate: number;
    showCallRate: number;
    mealPenaltyRate: number;
  };
  onRatesChange?: () => void; // Callback to mark all days as unsaved when rates change
}

type PayType = 'reg' | 'ot' | 'dt' | 'show' | 'meal';

interface DayData {
  regTimeHours?: number;
  overTimeHours?: number;
  doubleTimeHours?: number;
  showCallQty?: number;
  mealPenaltyQty?: number;
}

export const WeeklyEntryGrid: FC<WeeklyEntryGridProps> = ({
  allDetails,
  weekDates,
  isReadOnly,
  onDaySave,
  uniqueMemberId,
  stewardReportId,
  currentRates,
  onRatesChangeRef,
  onSaveAll,
}) => {
  // Local state for input values (for immediate UI updates)
  // NO API calls are made on cell edits - only when Save button is clicked
  const [localValues, setLocalValues] = useState<Map<string, string>>(new Map());
  // Track which days have unsaved changes
  const [unsavedDays, setUnsavedDays] = useState<Set<string>>(new Set());
  // Track which days are currently being saved
  const [savingDays, setSavingDays] = useState<Set<string>>(new Set());
  // Track if we're saving all days
  const [savingAll, setSavingAll] = useState(false);

  // Sync local values when allDetails changes (from external updates)
  // But preserve local values that were just set (to avoid overwriting user input)
  // In read-only mode, always sync from allDetails
  useEffect(() => {
    setLocalValues((prevLocalValues) => {
      const newValues = new Map<string, string>();
      
      // Debug logging in development
      if (process.env.NODE_ENV === 'development') {
        console.log('WeeklyEntryGrid: Syncing local values', {
          allDetailsCount: allDetails.length,
          uniqueMemberId,
          isReadOnly,
          weekDates: weekDates.map(d => d.toISOString().split('T')[0])
        });
      }
      
      allDetails.forEach((detail) => {
        // Filter by uniqueMemberId if provided
        if (uniqueMemberId && detail.uniqueMemberId !== uniqueMemberId) {
          return;
        }
        
        if (detail.dateFor) {
          const dateKey = normalizeDateFor(detail.dateFor);
          
          // Debug logging
          if (process.env.NODE_ENV === 'development') {
            console.log('WeeklyEntryGrid: Processing detail', {
              dateFor: detail.dateFor,
              normalizedDateKey: dateKey,
              regTimeHours: detail.regTimeHours,
              uniqueMemberId: detail.uniqueMemberId
            });
          }
          
          // In read-only mode, always sync from details
          // In edit mode, only sync values that aren't in local state (preserve user input)
          // Always set values, including 0 (0 is a valid value)
          // Note: We check !== undefined && !== null to allow 0 through
          if (detail.regTimeHours !== undefined && detail.regTimeHours !== null) {
            const key = `${dateKey}-reg`;
            if (isReadOnly || !prevLocalValues.has(key)) {
              newValues.set(key, detail.regTimeHours.toString());
            } else {
              newValues.set(key, prevLocalValues.get(key)!);
            }
          }
          if (detail.overTimeHours !== undefined && detail.overTimeHours !== null) {
            const key = `${dateKey}-ot`;
            if (isReadOnly || !prevLocalValues.has(key)) {
              newValues.set(key, detail.overTimeHours.toString());
            } else {
              newValues.set(key, prevLocalValues.get(key)!);
            }
          }
          if (detail.doubleTimeHours !== undefined && detail.doubleTimeHours !== null) {
            const key = `${dateKey}-dt`;
            if (isReadOnly || !prevLocalValues.has(key)) {
              newValues.set(key, detail.doubleTimeHours.toString());
            } else {
              newValues.set(key, prevLocalValues.get(key)!);
            }
          }
          if (detail.showCallQty !== undefined && detail.showCallQty !== null) {
            const key = `${dateKey}-show`;
            if (isReadOnly || !prevLocalValues.has(key)) {
              newValues.set(key, detail.showCallQty.toString());
            } else {
              newValues.set(key, prevLocalValues.get(key)!);
            }
          }
          if (detail.mealPenaltyQty !== undefined && detail.mealPenaltyQty !== null) {
            const key = `${dateKey}-meal`;
            if (isReadOnly || !prevLocalValues.has(key)) {
              newValues.set(key, detail.mealPenaltyQty.toString());
            } else {
              newValues.set(key, prevLocalValues.get(key)!);
            }
          }
        }
      });
      
      // Merge with existing local values to preserve user input (only in edit mode)
      if (!isReadOnly) {
        prevLocalValues.forEach((value, key) => {
          if (!newValues.has(key)) {
            newValues.set(key, value);
          }
        });
      }
      
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log('WeeklyEntryGrid: New local values', Array.from(newValues.entries()));
      }
      
      return newValues;
    });
  }, [allDetails, isReadOnly, uniqueMemberId, weekDates]);

  // Helper to normalize dateFor to just the date portion (YYYY-MM-DD)
  const normalizeDateFor = (dateFor: string | undefined): string => {
    if (!dateFor) return '';
    // If it's already just a date (YYYY-MM-DD), return as is
    if (dateFor.length === 10 && dateFor.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateFor;
    }
    // If it has time component, extract just the date part
    // Handle formats like "2026-12-12 00:00:00" or "2026-12-12T00:00:00" or ISO strings
    const datePart = dateFor.split(' ')[0].split('T')[0];
    // Validate it's a date format
    if (datePart.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return datePart;
    }
    // If parsing failed, try parsing as Date and converting back
    try {
      const parsed = new Date(dateFor);
      if (!isNaN(parsed.getTime())) {
        return parsed.toISOString().split('T')[0];
      }
    } catch (e) {
      // Ignore parse errors
    }
    return '';
  };

  // Get detail for a specific date
  const getDetailForDate = (date: Date): StewardReportDetail | null => {
    const dateStr = date.toISOString().split('T')[0];
    return allDetails.find((d) => {
      const matchesDate = normalizeDateFor(d.dateFor) === dateStr;
      const matchesMember = !uniqueMemberId || d.uniqueMemberId === uniqueMemberId;
      return matchesDate && matchesMember;
    }) || null;
  };

  // Mark a day as having unsaved changes (called on cell edit)
  // This only updates local state - NO API call is made
  const markDayUnsaved = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    setUnsavedDays((prev) => new Set(prev).add(dateStr));
  };

  // Mark all days as unsaved (called when rates change)
  // Expose this function via ref so parent can call it
  useEffect(() => {
    if (onRatesChangeRef) {
      onRatesChangeRef.current = () => {
        const allDateStrs = weekDates.map((date) => date.toISOString().split('T')[0]);
        setUnsavedDays((prev) => new Set(allDateStrs));
      };
    }
  }, [onRatesChangeRef, weekDates]);

  // Save all changes for a specific day
  // This is ONLY called when the user clicks the Save button
  // This is the ONLY place where detail records are created/updated via API
  const handleDaySave = async (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    const detail = getDetailForDate(date);

    // Build day data from local values - always set all fields
    const dayData: DayData = {
      regTimeHours: 0,
      overTimeHours: 0,
      doubleTimeHours: 0,
      showCallQty: 0,
      mealPenaltyQty: 0,
    };
    const isQuantity = (field: PayType) => field === 'show' || field === 'meal';

    payTypeRows.forEach((row) => {
      const key = `${dateStr}-${row.type}`;
      const localValue = localValues.get(key);
      
      // Always determine the value to use
      let numValue: number = 0;
      
      if (localValue !== undefined && localValue !== '') {
        // Use local value if it exists and is not empty
        numValue = isQuantity(row.type)
          ? parseInt(localValue) || 0
          : parseFloat(localValue) || 0;
      } else if (detail) {
        // Fall back to detail value if no local value
        switch (row.type) {
          case 'reg':
            numValue = detail.regTimeHours ?? 0;
            break;
          case 'ot':
            numValue = detail.overTimeHours ?? 0;
            break;
          case 'dt':
            numValue = detail.doubleTimeHours ?? 0;
            break;
          case 'show':
            numValue = detail.showCallQty ?? 0;
            break;
          case 'meal':
            numValue = detail.mealPenaltyQty ?? 0;
            break;
        }
      }
      // Otherwise numValue stays 0 (default)
      
      // Set the value (always set, even if 0)
      switch (row.type) {
        case 'reg':
          dayData.regTimeHours = numValue;
          break;
        case 'ot':
          dayData.overTimeHours = numValue;
          break;
        case 'dt':
          dayData.doubleTimeHours = numValue;
          break;
        case 'show':
          dayData.showCallQty = numValue;
          break;
        case 'meal':
          dayData.mealPenaltyQty = numValue;
          break;
      }
    });

    // Mark as saving
    setSavingDays((prev) => new Set(prev).add(dateStr));

    try {
      if (!uniqueMemberId) {
        console.error('Cannot save: uniqueMemberId is required');
        return;
      }
      
      // Include current rates from WorkerCard if available
      if (currentRates) {
        dayData.regTimeRate = currentRates.regTimeRate;
        dayData.showCallRate = currentRates.showCallRate;
        dayData.mealPenaltyRate = currentRates.mealPenaltyRate;
      }

      // Debug: log what we're passing
      console.log('WeeklyEntryGrid: Calling onDaySave with:', {
        date: dateStr,
        uniqueMemberId,
        dayData: {
          regTimeHours: dayData.regTimeHours,
          overTimeHours: dayData.overTimeHours,
          doubleTimeHours: dayData.doubleTimeHours,
          showCallQty: dayData.showCallQty,
          mealPenaltyQty: dayData.mealPenaltyQty,
          regTimeRate: dayData.regTimeRate,
          showCallRate: dayData.showCallRate,
          mealPenaltyRate: dayData.mealPenaltyRate,
        },
        localValues: Array.from(localValues.entries()).filter(([k]) => k.startsWith(dateStr))
      });
      
      await onDaySave(date, uniqueMemberId, dayData);
      // Clear unsaved flag on success
      setUnsavedDays((prev) => {
        const newSet = new Set(prev);
        newSet.delete(dateStr);
        return newSet;
      });
    } catch (err) {
      console.error('Error saving day:', err);
      // Keep unsaved flag on error
    } finally {
      // Clear saving flag
      setSavingDays((prev) => {
        const newSet = new Set(prev);
        newSet.delete(dateStr);
        return newSet;
      });
    }
  };

  // Save all unsaved days
  const handleSaveAll = async () => {
    if (!uniqueMemberId || unsavedDays.size === 0) return;

    setSavingAll(true);
    const daysToSave = Array.from(unsavedDays);
    
    try {
      // Save all unsaved days
      for (const dateStr of daysToSave) {
        const date = weekDates.find((d) => d.toISOString().split('T')[0] === dateStr);
        if (!date) continue;

        const detail = getDetailForDate(date);
        const dayData: DayData = {
          regTimeHours: 0,
          overTimeHours: 0,
          doubleTimeHours: 0,
          showCallQty: 0,
          mealPenaltyQty: 0,
        };
        const isQuantity = (field: PayType) => field === 'show' || field === 'meal';

        payTypeRows.forEach((row) => {
          const key = `${dateStr}-${row.type}`;
          const localValue = localValues.get(key);
          
          let numValue = 0;
          if (localValue !== undefined && localValue !== '') {
            numValue = isQuantity(row.type)
              ? parseInt(localValue) || 0
              : parseFloat(localValue) || 0;
          }
          
          switch (row.type) {
            case 'reg': dayData.regTimeHours = numValue; break;
            case 'ot': dayData.overTimeHours = numValue; break;
            case 'dt': dayData.doubleTimeHours = numValue; break;
            case 'show': dayData.showCallQty = numValue; break;
            case 'meal': dayData.mealPenaltyQty = numValue; break;
          }
        });

        // Include current rates from WorkerCard if available
        if (currentRates) {
          dayData.regTimeRate = currentRates.regTimeRate;
          dayData.showCallRate = currentRates.showCallRate;
          dayData.mealPenaltyRate = currentRates.mealPenaltyRate;
        }

        await onDaySave(date, uniqueMemberId, dayData);
      }

      // Clear all unsaved flags on success
      setUnsavedDays(new Set());
    } catch (err) {
      console.error('Error saving all days:', err);
    } finally {
      setSavingAll(false);
    }
  };

  // Get value for a specific field (prefer local state, fallback to detail)
  const getValue = (date: Date, detail: StewardReportDetail | null, field: PayType): string => {
    const dateStr = date.toISOString().split('T')[0];
    const key = `${dateStr}-${field}`;
    
    // Check local state first (for immediate UI updates)
    if (localValues.has(key)) {
      const value = localValues.get(key)!;
      if (process.env.NODE_ENV === 'development' && isReadOnly) {
        console.log('WeeklyEntryGrid getValue: Found in localValues', { key, value, field, dateStr });
      }
      return value;
    }

    // Fallback to detail value
    if (!detail) {
      if (process.env.NODE_ENV === 'development' && isReadOnly) {
        console.log('WeeklyEntryGrid getValue: No detail found', { key, field, dateStr, allDetailsCount: allDetails.length });
      }
      return '';
    }

    let value = '';
    switch (field) {
      case 'reg':
        value = detail.regTimeHours !== undefined && detail.regTimeHours !== null 
          ? detail.regTimeHours.toString() 
          : '';
        break;
      case 'ot':
        value = detail.overTimeHours !== undefined && detail.overTimeHours !== null 
          ? detail.overTimeHours.toString() 
          : '';
        break;
      case 'dt':
        value = detail.doubleTimeHours !== undefined && detail.doubleTimeHours !== null 
          ? detail.doubleTimeHours.toString() 
          : '';
        break;
      case 'show':
        value = detail.showCallQty !== undefined && detail.showCallQty !== null 
          ? detail.showCallQty.toString() 
          : '';
        break;
      case 'meal':
        value = detail.mealPenaltyQty !== undefined && detail.mealPenaltyQty !== null 
          ? detail.mealPenaltyQty.toString() 
          : '';
        break;
      default:
        value = '';
    }
    
    if (process.env.NODE_ENV === 'development' && isReadOnly) {
      console.log('WeeklyEntryGrid getValue: From detail', { key, value, field, dateStr, detailDateFor: detail.dateFor });
    }
    
    return value;
  };

  const payTypeRows: { type: PayType; label: string; step: string }[] = [
    { type: 'reg', label: 'Regular Time', step: '0.25' },
    { type: 'ot', label: 'Overtime', step: '0.25' },
    { type: 'dt', label: 'Double Time', step: '0.25' },
    { type: 'show', label: 'Show Call', step: '1' },
    { type: 'meal', label: 'Meal Penalty', step: '1' },
  ];

  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full align-middle">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-800 px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-r border-gray-200 dark:border-gray-700">
                Pay Type
              </th>
              {weekDates.map((date, idx) => (
                <th
                  key={idx}
                  className="px-3 py-3 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider min-w-[100px]"
                >
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 font-normal text-xs mt-1">
                      {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {payTypeRows.map((row, rowIdx) => {
              const isQuantity = row.type === 'show' || row.type === 'meal';
              return (
                <tr key={row.type} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="sticky left-0 z-10 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                    {row.label}
                    {isQuantity && (
                      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">(qty)</span>
                    )}
                  </td>
                  {weekDates.map((date, colIdx) => {
                    const detail = getDetailForDate(date);
                    const dateStr = date.toISOString().split('T')[0];
                    const valueKey = `${dateStr}-${row.type}`;
                    const value = getValue(date, detail, row.type);
                    const inputId = `grid-${row.type}-${dateStr}`;

                    return (
                      <td key={colIdx} className="px-2 py-2 text-center">
                        {isReadOnly ? (
                          <div className="text-sm text-gray-900 dark:text-white">
                            {value || '—'}
                          </div>
                        ) : (
                          <TextInput
                            id={inputId}
                            type="text"
                            inputMode={isQuantity ? "numeric" : "decimal"}
                            value={value}
                            onChange={(e) => {
                              const val = e.target.value.trim();
                              // Update local state immediately for UI responsiveness
                              setLocalValues((prev) => {
                                const newMap = new Map(prev);
                                newMap.set(valueKey, val);
                                return newMap;
                              });
                              // Mark day as having unsaved changes
                              markDayUnsaved(date);
                            }}
                            onBlur={(e) => {
                              // Format value on blur (no API call)
                              const val = e.target.value.trim();
                              let finalValue = val;
                              
                              if (val === '') {
                                finalValue = '';
                              } else {
                                const numVal = isQuantity
                                  ? Math.max(0, Math.floor(parseFloat(val) || 0))
                                  : Math.max(0, parseFloat(val) || 0);
                                
                                // Format: for hours, show 2 decimals; for quantities, show integer
                                finalValue = isQuantity
                                  ? numVal.toString()
                                  : numVal.toFixed(2).replace(/\.?0+$/, ''); // Remove trailing zeros
                              }
                              
                              // Update local state with formatted value
                              setLocalValues((prev) => {
                                const newMap = new Map(prev);
                                newMap.set(valueKey, finalValue);
                                return newMap;
                              });
                            }}
                            onKeyDown={(e) => {
                              // Allow: backspace, delete, tab, escape, enter, and decimal point (for hours)
                              if (
                                ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'].includes(e.key) ||
                                (e.key === '.' && !isQuantity) ||
                                (e.key >= '0' && e.key <= '9') ||
                                (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown')
                              ) {
                                return; // Allow these keys
                              }
                              // Prevent all other keys
                              e.preventDefault();
                            }}
                            className="w-full text-center text-sm"
                            placeholder="0"
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Save All button in lower left */}
      {!isReadOnly && (
        <div className="mt-2 flex items-center">
          <Button
            size="sm"
            onClick={handleSaveAll}
            disabled={unsavedDays.size === 0 || savingAll}
            color={unsavedDays.size > 0 ? 'blue' : 'gray'}
          >
            {savingAll ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              <>
                <HiOutlineCheck className="mr-2 h-4 w-4" />
                Save {unsavedDays.size > 0 ? `(${unsavedDays.size})` : ''}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

